import { z } from 'zod';
import {
  custoUnitarioSchema,
  dataHoraSchema,
  dataSchema,
  dinheiroSchema,
  quantidadeSchema,
  sentidoMovimentacaoEstoqueSchema,
  uuidSchema,
} from './comum.ts';
import { origemMovimentacaoEstoqueSchema } from './estoque.ts';

/**
 * Cardex — o extrato de movimentação de um insumo **em um depósito**
 * (`.spec/016`, seção 9; `.spec/010`, "Movimentação de estoque").
 *
 * É a leitura do livro-razão do estoque com o **saldo acumulado ao lado de cada
 * movimento**: quantidade, valor e custo médio como ficaram depois dele. É o
 * documento que o almoxarife confere contra a contagem física, e é a tela que
 * prova que o saldo materializado em `insumos_deposito` corresponde ao
 * histórico.
 *
 * Três decisões definem este contrato, e cada uma tem uma alternativa que
 * parecia mais simples:
 *
 * 1. **O insumo é obrigatório; o depósito, não.** Um cardex de vários materiais
 *    não tem coluna de saldo acumulado — não existe "o saldo" de cimento e areia
 *    somados. Já vários depósitos do **mesmo** material somam: quantidade e
 *    valor são aditivos, e o custo médio consolidado é a média ponderada dos
 *    locais, calculada na consulta e nunca gravada (`.spec/010`, última regra de
 *    "Movimentação de estoque"). Sem o depósito, a pergunta é "quanto deste
 *    material a empresa tem"; com ele, "o que aconteceu neste galpão".
 *
 * 2. **Não é paginado.** O acumulado da linha 26 depende das 25 anteriores, e
 *    uma página 2 que recomeçasse do zero mostraria um saldo que nunca existiu.
 *    O recorte que segura o tamanho é o próprio par insumo × depósito, e há um
 *    teto explícito (`LIMITE_MOVIMENTOS_CARDEX`) que recusa em vez de truncar.
 *
 * 3. **A ordem é a de gravação, e não a de data.** É a ordem que formou os
 *    saldos: a saída de terça usou o custo médio que a entrada de segunda
 *    ajudou a formar, mesmo que alguém tenha lançado a entrada depois com data
 *    retroativa. Ordenar por `dataLancamento` produziria uma coluna de custo
 *    médio plausível e falsa — números que o sistema nunca teve.
 */

/* -------------------------------------------------------------------------- */
/* Consulta                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Teto de movimentos que o cardex reconstrói numa consulta.
 *
 * O acumulado só se obtém replicando o histórico **desde a primeira entrada da
 * linha** — o custo médio não é `valor ÷ quantidade` do estado anterior, porque
 * a saída não o recalcula (`.spec/010`). Então o período filtra o que aparece,
 * não o que é lido, e o custo da consulta cresce com a vida do par insumo ×
 * depósito, não com o tamanho do período.
 *
 * O teto recusa em vez de truncar. Truncar devolveria um saldo anterior errado
 * sem avisar ninguém, e um cardex com saldo errado é pior que um cardex que não
 * abre — o segundo se percebe.
 */
export const LIMITE_MOVIMENTOS_CARDEX = 5000;

/**
 * Filtros do cardex.
 *
 * Sem `page` e sem `sort`: a decisão 2 e a decisão 3 do cabeçalho. Aceitar
 * `sort` seria oferecer ao usuário uma ordenação que torna a coluna de
 * acumulado mentirosa.
 *
 * O período é opcional dos dois lados. Ausente, o cardex mostra a vida inteira
 * da linha — que é o que se quer quando o insumo é novo, e é a única forma de o
 * saldo final da tela bater com o saldo do cadastro.
 */
// Estrito: parâmetro desconhecido é erro, não silêncio. Esta consulta não
// descende de `paginacaoSchema`, então carrega o seu. Ver o porquê lá.
export const consultarCardexQuerySchema = z
  .object({
    insumoId: uuidSchema,
    /**
     * Empresa do grupo. **Ausente traz todas.**
     *
     * Recorta os depósitos do extrato pela dona deles (`depositos.empresa_id`),
     * e é o filtro que faltava: material compartilhado tem linha de saldo em
     * empresas diferentes do grupo, e o extrato consolidado somava as duas sem
     * ninguém pedir — com o cabeçalho de empresa saindo em branco, que é a única
     * pista que havia.
     *
     * Com ela escolhida o extrato responde "o que esta empresa tem deste
     * material e por onde ele passou", que é a pergunta de quem fecha o estoque
     * de uma empresa. Sem ela, nada muda: continua sendo o grupo inteiro.
     *
     * Na tela ela também recorta a **busca do material** — só os insumos
     * vinculados àquela empresa aparecem —, e esse é o efeito que se pediu. O
     * recorte do extrato vem junto por coerência: escolher a empresa na busca e
     * receber o saldo de outra no resultado seria filtro que filtra pela metade.
     */
    empresaId: uuidSchema.optional(),
    /**
     * Depósito. **Ausente traz todos** os locais em que o insumo tem saldo.
     *
     * Ausente, e não uma palavra reservada como `'todos'`: a ausência já significa
     * "sem recorte" em toda listagem do sistema, e um código próprio obrigaria
     * cada camada a tratar dois jeitos de dizer a mesma coisa.
     */
    depositoId: uuidSchema.optional(),
    /** Início do período, inclusivo. Filtra `dataLancamento`. */
    de: dataSchema.optional(),
    /** Fim do período, inclusivo. Filtra `dataLancamento`. */
    ate: dataSchema.optional(),
  })
  .strict();

export type ConsultarCardexQuery = z.infer<typeof consultarCardexQuerySchema>;

/* -------------------------------------------------------------------------- */
/* Resposta                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Saldo em um ponto do extrato.
 *
 * Os três números andam juntos e nunca se separam: quantidade sem valor não
 * fecha com a contabilidade, e custo médio sem quantidade não se confere. É a
 * mesma trinca de `insumos_deposito`, sem o último custo — que é dado de
 * entrada de nota, não do extrato.
 */
export const saldoDoCardexSchema = z.object({
  quantidade: quantidadeSchema,
  valorEstoque: dinheiroSchema,
  /** Seis casas: é taxa, não dinheiro. Ver `custoUnitarioSchema`. */
  custoMedio: custoUnitarioSchema,
});

export type SaldoDoCardex = z.infer<typeof saldoDoCardexSchema>;

/**
 * Uma linha do extrato: o movimento **e o saldo que ele deixou**.
 *
 * Os três campos de acumulado são o cardex. Sem eles isto é a listagem de
 * movimentações, que já existe em outra tela.
 *
 * `insumoNome` não se repete por linha: é o mesmo em todas, e vive no cabeçalho
 * da resposta. O **depósito**, sim, porque sem ele o extrato de vários locais
 * não se lê — não há como saber de qual galpão saiu o material.
 */
export const linhaDoCardexSchema = z.object({
  id: uuidSchema,
  dataLancamento: dataSchema,
  depositoId: uuidSchema,
  depositoNome: z.string().nullable(),
  tipo: sentidoMovimentacaoEstoqueSchema,
  tipoMovimentacaoEstoqueNome: z.string().nullable(),
  /** Se este movimento formou custo. Cópia congelada do cadastro na gravação. */
  atualizaCusto: z.boolean(),
  origem: origemMovimentacaoEstoqueSchema,
  /** Id do documento de origem — o lançamento, hoje; a nota e o consumo, depois. */
  origemId: uuidSchema,
  /** Obra que recebeu o material. Preenchida no consumo. */
  obraId: uuidSchema.nullable(),
  obraNome: z.string().nullable(),
  /** Sempre positiva. O sentido está em `tipo`, nunca no sinal. */
  quantidade: quantidadeSchema,
  /** Na saída é o custo médio do momento, com as seis casas dele. */
  valorUnitario: custoUnitarioSchema,
  valorTotal: dinheiroSchema,
  /**
   * Saldo **depois** deste movimento, consolidando todos os depósitos do
   * extrato. Com um depósito só no filtro, é o saldo dele.
   */
  saldo: saldoDoCardexSchema,
  /**
   * Saldo do depósito **deste** movimento, depois dele.
   *
   * Existe ao lado do consolidado porque são perguntas diferentes num extrato de
   * vários locais: "quanto a empresa passou a ter" e "quanto sobrou neste
   * galpão". O custo médio daqui é o gravado em `insumos_deposito`; o do
   * consolidado é a média ponderada, que não se grava em lugar nenhum.
   */
  saldoDoDeposito: saldoDoCardexSchema,
  observacao: z.string(),
  /** Instante da gravação. É ele que ordena o extrato — ver a decisão 3. */
  createdAt: dataHoraSchema,
});

export type LinhaDoCardex = z.infer<typeof linhaDoCardexSchema>;

/**
 * Totais do período exibido.
 *
 * Quantidade **e** valor, nos dois sentidos. Só a quantidade não responde
 * "quanto de dinheiro saiu do canteiro em julho", e só o valor não se confere
 * contra a contagem física.
 */
export const totaisDoCardexSchema = z.object({
  quantidadeEntradas: quantidadeSchema,
  valorEntradas: dinheiroSchema,
  quantidadeSaidas: quantidadeSchema,
  valorSaidas: dinheiroSchema,
  /** `quantidadeEntradas − quantidadeSaidas`. O que o período movimentou. */
  quantidadeLiquida: quantidadeSchema,
  /** `valorEntradas − valorSaidas`. */
  valorLiquido: dinheiroSchema,
  /** Quantas linhas o período tem. É o `total` das listagens, sem paginação. */
  movimentos: z.number().int(),
});

export type TotaisDoCardex = z.infer<typeof totaisDoCardexSchema>;

/**
 * O cardex inteiro.
 *
 * O cabeçalho traz os nomes já resolvidos porque a exportação precisa deles: um
 * PDF que diga "Insumo 9f3a…-b21c" no título não serve para arquivar, e
 * resolver os nomes no cliente significaria duas consultas a mais só para
 * imprimir.
 *
 * `saldoAtual` é o número **materializado** em `insumos_deposito`, ao lado do
 * `saldoFinal` reconstruído. Os dois coincidem quando o período vai até hoje —
 * e quando não coincidem, o cardex é a tela que mostra isso. É a mesma
 * conferência do job `recalcula-saldos` (`.spec/004`, seção 3.1), disponível a
 * quem precisa dela agora.
 */
export const cardexSchema = z.object({
  insumoId: uuidSchema,
  insumoNome: z.string(),
  /** Sigla da unidade — entra nas colunas de quantidade e no cabeçalho. */
  unidadeSigla: z.string(),
  /** Decide se a quantidade se exibe com decimais. */
  unidadeApenasInteiros: z.boolean(),
  /**
   * O depósito filtrado, ou `null` quando o extrato abrange todos.
   *
   * A ausência é o que a tela e a exportação leem para decidir se a coluna de
   * depósito aparece: com um local só ela repetiria o mesmo nome em trezentas
   * linhas, e o cabeçalho já o diz uma vez.
   */
  depositoId: uuidSchema.nullable(),
  depositoNome: z.string().nullable(),
  /**
   * Quantos depósitos o extrato abrange.
   *
   * Não é `linhas.length` nem se deriva delas: um depósito com saldo e sem
   * movimento no período entra na conta e não aparece em linha nenhuma — e é ele
   * que explica a diferença entre o saldo anterior e a soma dos movimentos.
   */
  depositos: z.number().int(),
  /**
   * A empresa, quando o extrato é de uma só.
   *
   * `null` quando os depósitos são de empresas diferentes do grupo — o que
   * acontece com material compartilhado. Inventar "várias" como texto obrigaria
   * a exportação a distinguir isso de um nome de empresa.
   */
  empresaId: uuidSchema.nullable(),
  empresaNome: z.string().nullable(),

  /** Período pedido, devolvido como veio: é o que a exportação imprime. */
  de: dataSchema.nullable(),
  ate: dataSchema.nullable(),

  /**
   * Saldo **antes** do primeiro movimento exibido, na ordem de gravação.
   *
   * Sem ele o acumulado da primeira linha não se explica, e um cardex que
   * começasse em zero num período que não é o primeiro contradiria o saldo do
   * cadastro sem dizer por quê.
   */
  saldoAnterior: saldoDoCardexSchema,
  linhas: z.array(linhaDoCardexSchema),
  totais: totaisDoCardexSchema,
  /** Saldo depois do último movimento exibido. */
  saldoFinal: saldoDoCardexSchema,
  /**
   * O saldo gravado em `insumos_deposito`, para conferência lado a lado.
   *
   * Com vários depósitos é a consolidação dos gravados, pela **mesma** regra que
   * consolida o saldo final. É isso que faz os dois números baterem quando o
   * período vai até hoje: duas regras de consolidação diferentes acusariam
   * divergência onde não há.
   */
  saldoAtual: saldoDoCardexSchema,
});

export type Cardex = z.infer<typeof cardexSchema>;
